import { axios, getAxiosConfig } from '@config/axios';
import Environment from '@config/index';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { NegotiationEventRequest } from '@interfaces/models/negotiation/negotiationEventRequest';
import { ApiResponse } from '@interfaces/api';
import { UserTransactionRole } from '@interfaces/models/userTransactionRole';
import { BuyingOffersResponse } from '@interfaces/models/negotiation/buyingOffer';
import { SellingOffersProducts } from '@interfaces/models/domain/sellingOffersProducts';
import { getMappedSellingOffersNegotiationsResponse } from '@interfaces/models/mappers/sellingOffersNegotiationsMapper';
import { SellingOffersNegotiationsDTO } from '@interfaces/models/dtos/sellingOffersNegotiationsDTO';
import { getMappedSellingOffersProductListResponse } from '@interfaces/models/mappers/sellingOffersProductsMapper';
import { SellingOffersProductsDTO } from '@interfaces/models/dtos/sellingOffersProductsDTO';
import { SellingOffersNegotiations } from '@interfaces/models/domain/sellingOffersNegotiations';

export class NegotiationsService {
  static apiBaseUrl = Environment.apiBaseUrl;

  static async listNegotiations(role: UserTransactionRole, withEvents: boolean): Promise<Negotiation[]> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.withEvents = withEvents ?? null;

    const {
      data: { data },
    } = await axios.get<ApiResponse<Negotiation[]>>(`${this.apiBaseUrl}/negotiations`, axiosConfig);
    return data;
  }

  static async listBuyingOffersNegotiations(
    role: UserTransactionRole,
    limit: number,
    offset: number,
  ): Promise<BuyingOffersResponse> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<BuyingOffersResponse>(`${this.apiBaseUrl}/negotiations`, axiosConfig);

    return {
      data: response.data.data,
      meta: {
        count: response.data.meta.count,
        limit: response.data.meta.limit,
        offset: response.data.meta.offset,
      },
    };
  }

  static async listSellingOffersProducts(
    role: UserTransactionRole,
    limit: number,
    offset: number,
  ): Promise<SellingOffersProducts> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<SellingOffersProductsDTO>(
      `${this.apiBaseUrl}/users/me/products-negotiations`,
      axiosConfig,
    );

    return getMappedSellingOffersProductListResponse(response.data);
  }

  static async listSellingOffersNegotiations(
    id: number,
    role: UserTransactionRole,
    limit: number,
    offset: number,
  ): Promise<SellingOffersNegotiations> {
    const axiosConfig = getAxiosConfig();
    axiosConfig.params.role = role ?? null;
    axiosConfig.params.limit = limit ?? null;
    axiosConfig.params.offset = offset ?? null;

    const response = await axios.get<SellingOffersNegotiationsDTO>(
      `${this.apiBaseUrl}/users/me/products-negotiations/${id}/negotiations`,
      axiosConfig,
    );

    return getMappedSellingOffersNegotiationsResponse(response.data);
  }

  static async getNegotiation(id: Negotiation['id']): Promise<Negotiation | undefined> {
    const axiosConfig = getAxiosConfig();

    const {
      data: { data },
    } = await axios.get<ApiResponse<Negotiation | undefined>>(`${this.apiBaseUrl}/negotiations/${id}`, axiosConfig);

    return data || undefined;
  }

  static async addNegotiationEvent(
    id: Negotiation['id'],
    payload: NegotiationEventRequest,
  ): Promise<ApiResponse<Negotiation>> {
    const axiosConfig = getAxiosConfig();

    const { data } = await axios.post<ApiResponse<Negotiation>>(
      `${this.apiBaseUrl}/negotiations/${encodeURIComponent(String(id))}/events`,
      payload,
      axiosConfig,
    );
    return data;
  }

  static async executeFPD(
    id: Negotiation['id'],
    payload: NegotiationEventRequest | {},
  ): Promise<ApiResponse<Negotiation>> {
    const axiosConfig = getAxiosConfig();

    const { data } = await axios.post<ApiResponse<Negotiation>>(
      `${this.apiBaseUrl}/negotiations/${encodeURIComponent(String(id))}/flash-price-drop`,
      payload,
      axiosConfig,
    );
    return data;
  }
}
